import type { ProviderValidationStatus } from './providers/types'

/** One chat-connection state. Settings, chat, and the stage read this value. */
export type ChatConnectionPhase = 'ready' | 'missing-provider' | 'missing-model' | 'failed' | 'validating'

/** Wizard step that the status action opens. */
export type ChatConnectionStep = 'provider' | 'configure' | 'verify' | 'model'

export interface ChatConnectionNotice {
  providerId: string
  error: string
  checkedAt: string
}

const SECRET_TOKEN = /\bsk-[\w-]{8,}/g
const BEARER_TOKEN = /Bearer\s+\S+/g

/**
 * Removes credential text from a provider error.
 *
 * @example
 * sanitizeConnectionError('rejected sk-test-secret-value', ['sk-test-secret-value'])
 * // => 'rejected [redacted]'
 */
export function sanitizeConnectionError(message: string, secrets: string[] = []): string {
  let next = message
  for (const secret of secrets) {
    const trimmed = secret.trim()
    if (trimmed.length >= 6)
      next = next.split(trimmed).join('[redacted]')
  }
  return next.replace(SECRET_TOKEN, '[redacted]').replace(BEARER_TOKEN, 'Bearer [redacted]')
}

export function resolveChatConnectionPhase(input: {
  activeProviderId: string
  activeModelId: string
  providerStatus?: ProviderValidationStatus
  validating?: boolean
}): ChatConnectionPhase {
  if (input.validating || input.providerStatus === 'validating')
    return 'validating'
  if (!input.activeProviderId)
    return 'missing-provider'
  if (input.providerStatus === 'invalid')
    return 'failed'
  if (input.activeProviderId === 'prompt-api')
    return 'ready'
  if (!input.activeModelId)
    return 'missing-model'
  if (input.providerStatus === 'unconfigured')
    return 'missing-provider'
  return 'ready'
}

/** Stable send error for a blocked phase. A ready phase returns an empty string. */
export function chatConnectionSendError(phase: ChatConnectionPhase, failure = ''): string {
  switch (phase) {
    case 'missing-provider':
      return 'No chat provider is configured'
    case 'missing-model':
      return 'No chat model is selected'
    case 'failed':
      return failure.trim() || 'Chat provider verification failed'
    case 'validating':
      return 'Chat provider verification is running'
    case 'ready':
      return ''
  }
}

export function chatConnectionRoute(phase: ChatConnectionPhase): string {
  const step: ChatConnectionStep = phase === 'missing-model'
    ? 'model'
    : phase === 'failed' || phase === 'validating'
      ? 'verify'
      : 'provider'
  return `/settings/chat-connection?step=${step}`
}
